import React, { useEffect, useState, useCallback } from "react";
import { Divider } from "primereact/divider";
import CategoriesPerClientChart from "../../../components/admin/adminDashboard/dashboardCharts/CategoriesPerClientChart";
import TopCategoriesCharts from "../../../components/admin/adminDashboard/dashboardCharts/TopCategoriesCharts";
import ClientWiseCategory from "../../../components/admin/adminDashboard/dashboardTable/ClientWiseCategory";
import {
  getDashBoardData,
  getAssignedCategories,
  getCategoryPerClientGraph,
  getTopCategoryGraph,
} from "../../../services/admin/dashboard/dashboard";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import handleError from "../../../utils/handleError";
import { Skeleton } from "primereact/skeleton";
import ClientWiseCategorySkeleton from "../../../components/Skeletons/ClientWiseCategorySkeleton";

const AdminDashboard = () => {
  const { token, user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dashboardData, setDashboardData] = useState({
    total_categories: 0,
    total_clients: 0,
    assigned_categories: 0,
  });
  const [categoriesPerClientGraphData, setCategoriesByClientGraphData] =
    useState({});
  const [topCategoryGraphData, setTopCategoryGraphData] = useState({});
  const [categoriesByClient, setCategoriesByClient] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingChartData, setLoadingChartData] = useState(false);
  const [loadingTopChartData, setLoadingTopChartData] = useState(false);
  const [totalClients, setTotalClients] = useState(0);

  const fetchDashboardData = useCallback(() => {
    const formData = new FormData();
    formData.append("id", user.id);
    formData.append("type", user.type);
    getDashBoardData(token, formData)
      .then((response) => {
        if (response.data.success === 1) {
          setDashboardData({
            total_categories: response.data.total_categories,
            total_clients: response.data.total_clients,
            assigned_categories: response.data.assigned_categories,
          });
        } else {
          handleError(error, navigate, dispatch);
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      });
  }, [token, user.id, user.type, navigate, dispatch]);

  const fetchAssignedCategories = useCallback(() => {
    setLoading(true);
    const formData = new FormData();
    formData.append("id", user.id);
    formData.append("type", user.type);
    formData.append("page", page);
    formData.append("limit", limit);
    getAssignedCategories(token, formData)
      .then((response) => {
        if (response.data.success === 1) {
          setCategoriesByClient(response?.data.output || []);
          setTotalClients(response.data.total);
        } else {
          handleError(error, navigate, dispatch);
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, user.id, user.type, page, limit, navigate, dispatch]);

  const fetchCategoriesPerClientGraph = useCallback(() => {
    setLoadingChartData(true);
    const formData = new FormData();
    formData.append("id", user.id);
    formData.append("type", user.type);
    getCategoryPerClientGraph(token, formData)
      .then((response) => {
        if (response.data.success === 1) {
          setCategoriesByClientGraphData(response.data.output);
        } else {
          handleError(error, navigate, dispatch);
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      })
      .finally(() => {
        setLoadingChartData(false);
      });
  }, [token, user.id, user.type]);

  const fetchTopCategoriesGraph = useCallback(() => {
    setLoadingTopChartData(true);
    const formData = new FormData();
    formData.append("id", user.id);
    formData.append("type", user.type);
    getTopCategoryGraph(token, formData)
      .then((response) => {
        if (response.data.success === 1) {
          setTopCategoryGraphData(response.data.output);
        } else {
          handleError(error, navigate, dispatch);
        }
      })
      .catch((error) => {
        handleError(error, navigate, dispatch);
      })
      .finally(() => {
        setLoadingTopChartData(false);
      });
  }, [token, user.id, user.type]);

  useEffect(() => {
    fetchDashboardData();
    fetchCategoriesPerClientGraph();
    fetchTopCategoriesGraph();
  }, [
    fetchDashboardData,
    fetchCategoriesPerClientGraph,
    fetchTopCategoriesGraph,
  ]);

  useEffect(() => {
    fetchAssignedCategories();
  }, [fetchAssignedCategories]);

  const renderHeaderAndControls = () => (
    <div className={`d-flex justify-content-between align-items-center p-3`}>
      <h6 className="fw-bold m-0 title fs-14">View Dashboard Statistics</h6>
    </div>
  );

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalClients);
  const isPrevDisabled = page === 1;
  const isNextDisabled = end >= totalClients;

  const handlePrevPage = () => {
    if (!isPrevDisabled) {
      setPage((prev) => Math.max(prev - 1, 1));
    }
  };

  const handleNextPage = () => {
    if (!isNextDisabled) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <>
      <div className="container-fluid page-wrapper">
        <div
          className="card shadow-card"
          style={{ minHeight: "590px", marginBottom: "25px" }}
        >
          {renderHeaderAndControls()}
          <Divider
            style={{
              margin: "0.5rem 0 !important",
              padding: "0 1rem !important",
            }}
          />
          <div className="row m-4">
            <div
              className="col-12 col-custom"
              style={{ backgroundColor: "#3F51B5", marginRight: "1rem" }}
            >
              <p>Total Categories</p>
              {dashboardData.total_categories === 0 ? (
                <Skeleton size="3rem" className="mr-2"></Skeleton>
              ) : (
                <h1 className="fw-600">{dashboardData.total_categories}</h1>
              )}
            </div>
            <div
              className="col-12 col-custom"
              style={{ backgroundColor: "#2196F3", marginRight: "1rem" }}
            >
              <p>Clients</p>
              {dashboardData.total_clients === 0 ? (
                <Skeleton size="3rem" className="mr-2"></Skeleton>
              ) : (
                <h1 className="fw-600">{dashboardData.total_clients}</h1>
              )}
            </div>
            <div
              className="col-12 col-custom"
              style={{ backgroundColor: "#1E4C73" }}
            >
              <p>Assigned Categories</p>
              {dashboardData.assigned_categories === 0 ? (
                <Skeleton size="3rem" className="mr-2"></Skeleton>
              ) : (
                <h1 className="fw-600">{dashboardData.assigned_categories}</h1>
              )}
            </div>
          </div>
          <Divider />
          <h6 className="text-start ms-4 mt-4 fw-600 fs-14">
            Categories per client
          </h6>
          <CategoriesPerClientChart
            loading={loadingChartData}
            categoriesPerClientGraphData={categoriesPerClientGraphData}
          />
          <h6 className="text-start ms-4 fw-600 fs-14">
            Top 5 categories used
          </h6>
          <TopCategoriesCharts
            loading={loadingTopChartData}
            topCategoryGraphData={topCategoryGraphData}
          />
          <h6 className="text-start ms-4 fw-600 fs-14">
            Categories assigned by client
          </h6>
          {loading ? (
            <ClientWiseCategorySkeleton />
          ) : (
            <ClientWiseCategory
              start={start}
              end={end}
              totalClients={totalClients}
              isPrevDisabled={isPrevDisabled}
              isNextDisabled={isNextDisabled}
              handlePrevPage={handlePrevPage}
              handleNextPage={handleNextPage}
              categoriesByClient={categoriesByClient}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;
